/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : useScrollButtons.ts
// 스크롤 상태를 관찰해서 이동 버튼 표시 여부를 계산하는 훅 3종
//  - useContentScrollButtons : 본문(.custom-scrollbar.active) 맨위/맨아래 버튼
//  - useTabScrollButtons     : 탭 스트립(#mainContentOpenTabs) 좌/우 스크롤 버튼
//  - useSidebarScrollButtons : 좌측 메인사이드바 메뉴(#mainMenu) 상/하 스크롤 버튼
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useEffect, useState, type DependencyList } from 'react';

export function useContentScrollButtons(deps: DependencyList): { showScrollToTop: boolean; showScrollToBottom: boolean } {
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState<boolean>(false);

  // 스크롤바 자동 숨김/표시 기능 및 맨 위로 가기 버튼
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      // document/window 레벨 스크롤(target이 Element가 아닌 Document 등)은 classList 자체가 없어
      // 그냥 무시함 - capture:true라 페이지 내 모든 스크롤을 다 받다 보니(예: 새로 추가된 SunEditor
      // 내부 스크롤/전체화면 모드), .custom-scrollbar가 아닌 대상도 여기로 들어올 수 있음.
      if (target && target.classList && target.classList.contains('custom-scrollbar')) {
        // 스크롤 중일 때 스크롤바 표시
        target.classList.add('scrolling');

        // 스크롤 위치 확인하여 버튼 표시/숨김
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // 스크롤 가능한 콘텐츠가 있는지 확인
        const hasScrollableContent = scrollHeight > clientHeight;

        if (hasScrollableContent) {
          // 스크롤이 전체 높이의 30% 이상 내려갔을 때 위로 가기 버튼 표시
          const scrollPercentage = scrollTop / (scrollHeight - clientHeight);
          setShowScrollToTop(scrollPercentage > 0.3);

          // 스크롤이 전체 높이의 70% 이하일 때 아래로 가기 버튼 표시
          setShowScrollToBottom(scrollPercentage < 0.7);
        } else {
          // 스크롤할 콘텐츠가 없으면 버튼 숨기기
          setShowScrollToTop(false);
          setShowScrollToBottom(false);
        }

        // 기존 타이머 클리어
        clearTimeout(scrollTimeout);

        // 1.5초 후 스크롤바 숨김
        scrollTimeout = setTimeout(() => {
          target.classList.remove('scrolling');
        }, 1500);
      }
    };

    const handleWheel = (event: WheelEvent) => {
      const target = event.target as HTMLElement;
      const scrollableElement = target?.closest?.('.custom-scrollbar') as HTMLElement | null;

      if (scrollableElement) {
        // 휠 이벤트 시 스크롤바 표시
        scrollableElement.classList.add('scrolling');

        // 스크롤 가능한 콘텐츠 확인 및 버튼 상태 업데이트
        const scrollHeight = scrollableElement.scrollHeight;
        const clientHeight = scrollableElement.clientHeight;
        const hasScrollableContent = scrollHeight > clientHeight;

        if (hasScrollableContent) {
          const scrollTop = scrollableElement.scrollTop;
          const scrollPercentage = scrollTop / (scrollHeight - clientHeight);
          setShowScrollToTop(scrollPercentage > 0.3);
          setShowScrollToBottom(scrollPercentage < 0.7);
        } else {
          setShowScrollToTop(false);
          setShowScrollToBottom(false);
        }

        // 기존 타이머 클리어
        clearTimeout(scrollTimeout);

        // 1.5초 후 스크롤바 숨김
        scrollTimeout = setTimeout(() => {
          scrollableElement.classList.remove('scrolling');
        }, 1500);
      }
    };

    // 탭 변경 시 버튼 상태 체크 함수
    const checkScrollButtons = () => {
      const activeTabElement = document.querySelector('.custom-scrollbar.active') as HTMLElement;
      if (activeTabElement) {
        const scrollHeight = activeTabElement.scrollHeight;
        const clientHeight = activeTabElement.clientHeight;
        const hasScrollableContent = scrollHeight > clientHeight;

        if (hasScrollableContent) {
          const scrollTop = activeTabElement.scrollTop;
          const scrollPercentage = scrollTop / (scrollHeight - clientHeight);
          setShowScrollToTop(scrollPercentage > 0.3);
          setShowScrollToBottom(scrollPercentage < 0.7);
        } else {
          setShowScrollToTop(false);
          setShowScrollToBottom(false);
        }
      } else {
        setShowScrollToTop(false);
        setShowScrollToBottom(false);
      }
    };

    // 초기 체크 및 탭 변경 감지
    checkScrollButtons();

    // ResizeObserver로 콘텐츠 크기 변화 감지
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(checkScrollButtons, 100); // 약간의 지연을 두어 렌더링 완료 후 체크
    });

    // 모든 custom-scrollbar 요소 관찰
    const scrollableElements = document.querySelectorAll('.custom-scrollbar');
    scrollableElements.forEach(element => {
      resizeObserver.observe(element);
    });

    // 이벤트 리스너 등록
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('wheel', handleWheel, true);

    return () => {
      // 클린업
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('wheel', handleWheel, true);
      resizeObserver.disconnect();
      clearTimeout(scrollTimeout);
    };
  }, deps); // 호출부에서 넘긴 의존성(예: [openTabs]) 변경 시 재체크

  return { showScrollToTop, showScrollToBottom };
}

export function useTabScrollButtons(deps: DependencyList): { showTabScrollLeft: boolean; showTabScrollRight: boolean } {
  const [showTabScrollLeft, setShowTabScrollLeft] = useState<boolean>(false);
  const [showTabScrollRight, setShowTabScrollRight] = useState<boolean>(false);

  // 탭 스크롤 상태 체크
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const checkTabScroll = () => {
      const tabContainer = document.getElementById('mainContentOpenTabs');
      if (tabContainer) {
        const hasHorizontalScroll = tabContainer.scrollWidth > tabContainer.clientWidth;
        const scrollLeft = tabContainer.scrollLeft;
        const maxScrollLeft = tabContainer.scrollWidth - tabContainer.clientWidth;

        setShowTabScrollLeft(hasHorizontalScroll && scrollLeft > 0);
        setShowTabScrollRight(hasHorizontalScroll && scrollLeft < maxScrollLeft);
      } else {
        setShowTabScrollLeft(false);
        setShowTabScrollRight(false);
      }
    };

    // 초기 체크
    checkTabScroll();

    // 탭 컨테이너에 스크롤 이벤트 리스너 추가
    const tabContainer = document.getElementById('mainContentOpenTabs');
    if (tabContainer) {
      tabContainer.addEventListener('scroll', checkTabScroll);
    }

    // ResizeObserver로 탭 컨테이너 크기 변화 감지
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(checkTabScroll, 100);
    });

    if (tabContainer) {
      resizeObserver.observe(tabContainer);
    }

    return () => {
      if (tabContainer) {
        tabContainer.removeEventListener('scroll', checkTabScroll);
      }
      resizeObserver.disconnect();
    };
  }, deps); // 호출부에서 넘긴 의존성(예: [openTabs]) 변경 시 재체크

  return { showTabScrollLeft, showTabScrollRight };
}

// 좌측 메인사이드바 메뉴(#mainMenu) 세로 스크롤 버튼 - 브라우저가 작아지거나 메뉴 항목이 많아져서
// 목록이 넘칠 때, 스크롤바 대신 상/하 버튼(이미지 기준 초록색 바 + 화살표)으로 넘겨보게 함.
export function useSidebarScrollButtons(deps: DependencyList): { showSidebarScrollUp: boolean; showSidebarScrollDown: boolean } {
  const [showSidebarScrollUp, setShowSidebarScrollUp] = useState<boolean>(false);
  const [showSidebarScrollDown, setShowSidebarScrollDown] = useState<boolean>(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const checkSidebarScroll = () => {
      const menuContainer = document.getElementById('mainMenu');
      if (menuContainer) {
        const hasVerticalScroll = menuContainer.scrollHeight > menuContainer.clientHeight;
        const scrollTop = menuContainer.scrollTop;
        const maxScrollTop = menuContainer.scrollHeight - menuContainer.clientHeight;

        setShowSidebarScrollUp(hasVerticalScroll && scrollTop > 0);
        setShowSidebarScrollDown(hasVerticalScroll && scrollTop < maxScrollTop - 1); // 서브픽셀 오차 여유
      } else {
        setShowSidebarScrollUp(false);
        setShowSidebarScrollDown(false);
      }
    };

    checkSidebarScroll();

    const menuContainer = document.getElementById('mainMenu');
    if (menuContainer) {
      menuContainer.addEventListener('scroll', checkSidebarScroll);
    }

    // ResizeObserver로 메뉴 컨테이너 크기 변화(브라우저 축소/확대, 메뉴 항목 수 변화) 감지.
    // 사이드바 확장/축소 클릭 시 width 트랜지션(0.3s) 도중에도 매 프레임 resize가 계속 발생해서,
    // 디바운스 없이 매번 setTimeout만 걸면 중간 상태값들이 서로 경합하며 상/하 버튼이 깜빡이는
    // 문제가 있었음(2026-09-17) - 이전 타이머를 매번 취소하고 마지막 resize 이후에만 1번 체크하도록 수정.
    let resizeDebounce: ReturnType<typeof setTimeout>;
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeDebounce);
      resizeDebounce = setTimeout(checkSidebarScroll, 350); // 확장/축소 트랜지션(0.3s)이 끝난 뒤 체크
    });
    if (menuContainer) {
      resizeObserver.observe(menuContainer);
    }

    return () => {
      if (menuContainer) {
        menuContainer.removeEventListener('scroll', checkSidebarScroll);
      }
      resizeObserver.disconnect();
      clearTimeout(resizeDebounce);
    };
  }, deps);

  return { showSidebarScrollUp, showSidebarScrollDown };
}
